import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

const EDGE_URL = 'https://yaeuwvupxiknfkxvgwdx.supabase.co/functions/v1/get-ip-region';

function getSessionId(): string {
  let sid = sessionStorage.getItem('_sid');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('_sid', sid);
  }
  return sid;
}

function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/tablet|ipad/i.test(ua)) return '平板端';
  if (/mobile|android|iphone/i.test(ua)) return '移动端';
  return '桌面端';
}

// 缓存 IP + 地区信息，整个会话只请求一次
interface IpInfo {
  ip: string;
  region: string; // 省份，如"广东"
  city: string;   // 城市，如"广州"
}

let _cachedIpInfo: IpInfo | null = null;
let _ipFetching = false;
let _ipCallbacks: ((info: IpInfo) => void)[] = [];

async function getIpInfo(): Promise<IpInfo> {
  if (_cachedIpInfo) return _cachedIpInfo;

  return new Promise((resolve) => {
    _ipCallbacks.push(resolve);

    if (_ipFetching) return;
    _ipFetching = true;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);

    fetch(EDGE_URL, { signal: controller.signal })
      .then((res) => res.json())
      .then((data: { ip?: string; region?: string; city?: string }) => {
        clearTimeout(timer);
        const info: IpInfo = {
          ip: data.ip || '',
          region: data.region || '未知地区',
          city: data.city || '',
        };
        _cachedIpInfo = info;
        _ipCallbacks.forEach((cb) => cb(info));
        _ipCallbacks = [];
      })
      .catch(() => {
        clearTimeout(timer);
        const fallback: IpInfo = { ip: '', region: '未知地区', city: '' };
        _cachedIpInfo = fallback;
        _ipCallbacks.forEach((cb) => cb(fallback));
        _ipCallbacks = [];
      });
  });
}

export function useSiteTracker() {
  const location = useLocation();

  useEffect(() => {
    const record = async () => {
      try {
        const ipInfo = await getIpInfo();

        // region 存"省份·城市"格式，城市与省份相同时只存省份
        const regionLabel =
          ipInfo.city && ipInfo.city !== ipInfo.region
            ? `${ipInfo.region}·${ipInfo.city}`
            : ipInfo.region;

        await supabase.from('site_visits').insert({
          page_path: location.pathname,
          page_title: document.title,
          referrer: document.referrer || null,
          session_id: getSessionId(),
          device_type: getDeviceType(),
          ip_hash: ipInfo.ip,
          region: regionLabel,
        });
      } catch {
        // 埋点失败不影响用户体验
      }
    };
    record();
  }, [location.pathname]);
}
